import React, { useEffect, useState } from 'react';
import './help.css';
import './status.css';

const SERVICE_TARGETS = [
  {
    id: 'api',
    name: 'Backend API',
    url: '/healthz',
    description: 'Primary REST API that powers the app UI.',
  },
  {
    id: 'frontend',
    name: 'Web client',
    url: window.location.origin,
    description: 'Static assets served from the current origin.',
    skipPing: true,
  },
];

const Status = () => {
  const [checks, setChecks] = useState(
    SERVICE_TARGETS.map((target) => ({
      ...target,
      status: target.skipPing ? 'ok' : 'checking',
      latency: null,
      checkedAt: null,
      error: null,
    })),
  );

  useEffect(() => {
    let cancelled = false;

    const runChecks = async () => {
      const results = await Promise.all(
        SERVICE_TARGETS.map(async (target) => {
          if (target.skipPing) {
            return {
              ...target,
              status: 'ok',
              latency: 0,
              checkedAt: new Date().toISOString(),
              error: null,
            };
          }
          const started = performance.now();
          try {
            const response = await fetch(target.url, { method: 'GET' });
            const latency = Math.round(performance.now() - started);
            if (!response.ok) {
              throw new Error(response.statusText || 'Request failed');
            }
            return {
              ...target,
              status: 'ok',
              latency,
              checkedAt: new Date().toISOString(),
              error: null,
            };
          } catch (error) {
            return {
              ...target,
              status: 'down',
              latency: null,
              checkedAt: new Date().toISOString(),
              error: error.message,
            };
          }
        }),
      );
      if (!cancelled) {
        setChecks(results);
      }
    };

    runChecks();
    const interval = window.setInterval(runChecks, 60 * 1000);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  return (
    <section className="status-page">
      <header className="status-hero">
        <div>
          <p className="status-hero__eyebrow">Platform status</p>
          <h1>All systems operational</h1>
          <p>We monitor uptime around the clock. Check component health in real time.</p>
        </div>
        <a href="/help" className="help-button help-button--secondary">
          Return to Help page
        </a>
      </header>

      <section className="status-components">
        {checks.map((check) => (
          <article key={check.id} className="status-component-card">
            <div>
              <h2>{check.name}</h2>
              <p>{check.description}</p>
              <small>
                Checked {check.checkedAt ? new Date(check.checkedAt).toLocaleTimeString() : '—'}
              </small>
            </div>
            <div className="status-component-card__meta">
              <span className={`status-pill status-pill--${check.status}`}>
                {check.status === 'ok' ? 'Operational' : check.status === 'checking' ? 'Checking' : 'Down'}
              </span>
              <span className="status-latency">
                {check.latency != null ? `${check.latency} ms` : '—'}
              </span>
              {check.error && <p className="status-error">{check.error}</p>}
            </div>
          </article>
        ))}
      </section>
    </section>
  );
};

export default Status;
